/*
 |-----------------------------------------------------------------------------
 | Los roles, dichos desde el lado del estudio
 |-----------------------------------------------------------------------------
 | En la base son `owner`, `admin_tenant`, `member` y `client`. Eso describe el
 | modelo de permisos, no el organigrama de una agencia de fotografía, y ponerlo
 | tal cual en un desplegable obliga al dueño a deducir qué implica «member».
 |
 | Aquí se dice qué PUEDE HACER cada uno, que es la pregunta real: a quién dejo
 | tocar la facturación y a quién sólo las fotos.
 */

export type RoleId = 'owner' | 'admin_tenant' | 'member' | 'client'

/**
 * Los que un administrador puede ASIGNAR.
 *
 * `owner` no está: hay uno y es quien dio de alta el estudio. La API tampoco lo
 * acepta —`in:admin_tenant,member,client`—, así que un desplegable tipado como
 * `RoleId` permitía construir una petición que el servidor devuelve como 422.
 * Se descubrió al tipar el cuerpo de `POST /users` con el tipo generado.
 */
export type AssignableRoleId = Exclude<RoleId, 'owner'>

export interface RoleInfo {
  label: string
  /** Qué alcanza. Se lee al elegir. */
  summary: string
  /** Si un administrador puede asignarlo. `owner` no: hay uno y es el dueño. */
  assignable: boolean
}

export const ROLES: Record<RoleId, RoleInfo> = {
  owner: {
    label: 'Propietario',
    summary: 'Todo, incluida la facturación. Es la cuenta que dio de alta el estudio.',
    assignable: false,
  },
  admin_tenant: {
    label: 'Administrador',
    summary: 'Todo: proyectos, equipo, marca y facturación.',
    assignable: true,
  },
  member: {
    label: 'Fotógrafo',
    summary: 'Crea proyectos, sube archivos y comparte enlaces. No toca la facturación ni el equipo.',
    assignable: true,
  },
  client: {
    label: 'Invitado',
    summary: 'Sólo ve los proyectos a los que se le añade. No puede crear ni subir.',
    assignable: true,
  },
}

export const ASSIGNABLE_ROLES = (Object.keys(ROLES) as RoleId[]).filter(
  (id): id is AssignableRoleId => ROLES[id].assignable,
)

export function roleLabel(role: string): string {
  return ROLES[role as RoleId]?.label ?? role
}
